import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PageHeader, StatGrid, StatTile, PanelGrid, Panel, SimpleTable, RankList, Button, Banner, exportRows,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';

/**
 * דוחות — new design. Same four endpoints as Reports; each panel exports its own table to Excel.
 */
const num = (v) => Number(v || 0).toLocaleString('he-IL');
const pct = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `${Number(v).toFixed(1)}%`);
const memberName = (m) => (m ? `${m.firstName || ''} ${m.lastName || ''}`.trim() || '—' : '—');

const EXPORTS = {
  byCity: { filename: 'נבחנים_לפי_עיר', columns: [{ key: 'city', label: 'עיר' }, { key: 'count', label: 'מספר חברים' }] },
  top90: {
    filename: 'ציוני_90_פלוס',
    columns: [
      { key: 'memberId', label: 'שם', getValue: (r) => memberName(r.memberId) },
      { key: 'examId', label: 'מבחן', getValue: (r) => r.examId?.name || '' },
      { key: 'score', label: 'ציון' },
    ],
  },
  passRates: {
    filename: 'אחוזי_מעבר',
    columns: [{ key: 'group', label: 'קבוצה' }, { key: 'total', label: 'סה"כ' }, { key: 'passed', label: 'עברו' }, { key: 'passRate', label: 'אחוז מעבר', getValue: (r) => pct(r.passRate) }],
  },
  avg: { filename: 'ציונים_ממוצעים', columns: [{ key: 'examType', label: 'סוג מבחן' }, { key: 'avgScore', label: 'ציון ממוצע' }, { key: 'count', label: 'מספר ציונים' }] },
};

export default function ReportsNext() {
  const [d, setD] = useState({ byCity: [], top90: [], passRates: [], avg: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [byCity, top90, passRates, avg] = await Promise.all([
        api.get('/reports/by-city'),
        api.get('/reports/top90'),
        api.get('/reports/pass-rates'),
        api.get('/reports/avg-scores'),
      ]);
      setD({ byCity: byCity.data.data || [], top90: top90.data.data || [], passRates: passRates.data.data || [], avg: avg.data.data || [] });
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בטעינת הדוחות');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const members = d.byCity.reduce((s, r) => s + (Number(r.count) || 0), 0);
    const graded = d.avg.reduce((s, r) => s + (Number(r.count) || 0), 0);
    const passTotal = d.passRates.reduce((s, r) => s + (Number(r.total) || 0), 0);
    const passed = d.passRates.reduce((s, r) => s + (Number(r.passed) || 0), 0);
    return { members, graded, passRate: passTotal ? (passed / passTotal) * 100 : null, top90: d.top90.length };
  }, [d]);

  const exportOf = (key) => () => exportRows(d[key], EXPORTS[key].columns, { format: 'xlsx', filename: EXPORTS[key].filename });
  const exportBtn = (key) => <Button size="sm" variant="ghost" icon="download" disabled={!d[key].length} onClick={exportOf(key)}>ייצוא</Button>;

  return (
    <>
      <PageHeader
        title="דוחות"
        sub="נבחנים, ציונים ואחוזי מעבר"
        more={[
          { key: 'refresh', label: 'רענון', icon: 'refresh', onClick: load },
          { divider: true },
          ...Object.keys(EXPORTS).map((k) => ({ key: `x-${k}`, label: `ייצוא — ${EXPORTS[k].filename.replace(/_/g, ' ')}`, icon: 'download', onClick: exportOf(k), disabled: !d[k].length })),
        ]}
      />
      {error ? <Banner tone="danger" icon="alert" action={<Button size="sm" onClick={load}>נסו שוב</Button>}>{error}</Banner> : null}

      <StatGrid min={190}>
        <StatTile label="חברים לפי עיר" value={num(totals.members)} sub={`${num(d.byCity.length)} ערים`} icon="users" tone="primary" loading={loading} />
        <StatTile label="ציונים שהוזנו" value={num(totals.graded)} sub={`${num(d.avg.length)} סוגי מבחן`} icon="task" tone="violet" loading={loading} />
        <StatTile label="אחוז מעבר כולל" value={pct(totals.passRate)} icon="check" tone="success" loading={loading} />
        <StatTile label="ציוני 90+" value={num(totals.top90)} icon="sparkle" tone="warning" loading={loading} />
      </StatGrid>

      <PanelGrid cols={2}>
        <Panel title="נבחנים לפי עיר" icon="flag" actions={exportBtn('byCity')}>
          <RankList loading={loading} formatValue={num} items={d.byCity.map((r, i) => ({ key: r.city || i, label: r.city || '—', value: r.count }))} />
        </Panel>
        <Panel title="ציוני 90+" sub={d.top90.length > 20 ? `20 הראשונים מתוך ${num(d.top90.length)}` : undefined} icon="sparkle" tone="warning" flush actions={exportBtn('top90')}>
          <SimpleTable
            loading={loading}
            rowKey={(r) => r._id || `${r.memberId?._id}-${r.examId?._id}`}
            rows={d.top90.slice(0, 20)}
            maxHeight={420}
            columns={[
              { key: 'memberId', label: 'שם', render: (r) => <b>{memberName(r.memberId)}</b> },
              { key: 'examId', label: 'מבחן', render: (r) => r.examId?.name || '—' },
              { key: 'score', label: 'ציון', align: 'center' },
            ]}
          />
        </Panel>
        <Panel title="אחוזי מעבר לפי סוג" icon="check" tone="success" actions={exportBtn('passRates')}>
          <RankList
            loading={loading}
            tone="success"
            formatValue={pct}
            items={d.passRates.map((r, i) => ({ key: r.group || i, label: r.group || '—', sub: `${num(r.passed)} מתוך ${num(r.total)}`, value: r.passRate }))}
          />
        </Panel>
        <Panel title="ציון ממוצע לפי סוג" icon="trend" tone="info" flush actions={exportBtn('avg')}>
          <SimpleTable
            loading={loading}
            rowKey={(r) => r.examType || r._id}
            rows={d.avg}
            columns={[
              { key: 'examType', label: 'סוג מבחן', render: (r) => <b>{r.examType || '—'}</b> },
              { key: 'avgScore', label: 'ציון ממוצע', align: 'center', render: (r) => (r.avgScore == null ? '—' : Number(r.avgScore).toFixed(1)) },
              { key: 'count', label: 'מספר ציונים', align: 'center', render: (r) => num(r.count) },
            ]}
          />
        </Panel>
      </PanelGrid>
    </>
  );
}
